import { Adornment, DatePicker, DatePickerInput, IconCalendarOutline } from '@zillow/constellation';
import { addWeeks, format, isAfter, isBefore, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import type Constellation from '@zillow/constellation';

import { CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING } from '../../../../constants';
import { FlexGrowAdornedInput } from './StyledComponents';
type DatePickerMetaProps = Constellation.DatePickerMetaProps;

export const getDisabledDates = (disabledDates: Array<string>) => {
  return (date: Date) => {
    const isOutOfRange = isBefore(date, startOfDay(new Date())) || isAfter(date, addWeeks(new Date(), 3));
    const isAlreadySelected = disabledDates.some((disabledDate) => {
      const disabledDateObj = new Date(disabledDate);
      return format(date, 'MM/dd/yyyy') === format(disabledDateObj, 'MM/dd/yyyy');
    });
    return isOutOfRange || isAlreadySelected;
  };
};

type FormattedDatePickerInputProps = {
  date: string;
  onChangeDate: (date: string, meta: DatePickerMetaProps) => void;
  dataTestId?: string;
  disabledDates?: Array<string>;
};

const FormattedDatePickerInput = ({
  date,
  onChangeDate,
  dataTestId,
  disabledDates = [],
}: FormattedDatePickerInputProps) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const datePickerInputValue = useMemo(() => {
    if (isInputFocused) {
      return date;
    }
    return format(new Date(date), CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING);
  }, [date, isInputFocused]);

  return (
    <FlexGrowAdornedInput
      // onFocus on AdornedInput and onBlur on DatePickerInput is important/intentional due to how events are ordered between the two inputs
      onFocus={() => setIsInputFocused(true)}
      fluid
      input={
        <DatePickerInput
          value={datePickerInputValue}
          onBlur={() => setIsInputFocused(false)}
          onChange={onChangeDate}
          datepicker={
            <DatePicker
              disabledDates={getDisabledDates(disabledDates)}
              minDate={new Date()}
              maxDate={addWeeks(new Date(), 3)}
            />
          }
          data-testid={dataTestId ?? 'FormattedDatePickerInput'}
        />
      }
      rightAdornment={
        <Adornment>
          <IconCalendarOutline size="sm" />
        </Adornment>
      }
    />
  );
};

export default FormattedDatePickerInput;
